/**
 * useVoicePairing — hook do ditado por voz via QR pairing (web /ia, Fase Voice S2).
 *
 * Espelha apps/extension/src/sidebar/hooks/useVoicePairing.ts, adaptado para o
 * stack do web app:
 *   - JWT vem do localStorage via `getStoredJwt()` (api/client.ts), não de
 *     chrome.storage.local.
 *   - Sem `.js` nos imports (Vite resolve TS direto).
 *   - React 19 + tipos públicos via `readonly` properties.
 *
 * Responsabilidade:
 *   - POST /api/voice/pair-init → recebe { token, qr_url, expiresAt }
 *   - Conecta SSE em /api/voice/stream/:token (via fetch + ReadableStream,
 *     porque EventSource nativo não permite header Authorization customizado).
 *   - Escuta eventos: pairing.connected / recording.started / transcribed /
 *     error / expired.
 *   - Callback onTranscribed disparado quando texto chega — App.tsx wirea
 *     pro `chat.sendUserMessage` automaticamente.
 *
 * Tier gate: 403 com error 'tier_upgrade_required' → state='tier_blocked'.
 *
 * Cleanup: useEffect com abort controller fecha SSE no unmount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getStoredJwt } from '../api/client';

/* ------------------------------------------------------------------------- */
/* Tipos                                                                       */
/* ------------------------------------------------------------------------- */

/** Estado da máquina de pareamento (visível à UI). */
export type VoiceState =
  | 'idle'           // sem pareamento ativo
  | 'requesting'     // POST pair-init em curso
  | 'waiting'        // QR mostrado, aguardando mobile escanear
  | 'paired'         // mobile conectou
  | 'recording'      // mobile gravando
  | 'transcribing'   // áudio em Whisper
  | 'disconnected'   // SSE caiu DEPOIS de paired — recuperável via reconectar()
  | 'error'
  | 'tier_blocked';  // user free/plantonista

/** Evento parseado do SSE stream. */
export interface VoiceSSEEvent {
  readonly type: string;
  readonly data: Record<string, unknown>;
  readonly seq: number | null;
}

/** Callback chamado quando 'transcribed' chega do mobile. */
export type OnTranscribedCb = (text: string, audioSeconds: number) => void;

/** Resultado do hook (público). */
export interface UseVoicePairingResult {
  readonly state: VoiceState;
  readonly token: string | null;
  readonly qrUrl: string | null;
  readonly expiresAt: number | null;
  readonly lastTranscript: string | null;
  readonly error: string | null;
  readonly upgradeUrl: string | null;
  /**
   * Inicia pareamento. `opts.locale` (opcional) anexa `?lang=<locale>` na URL
   * do mic mobile pra que a página /mic/:token abra direto no idioma do médico
   * (em vez de PT-BR fallback). Fix briefing 23/05 noite (Dr. Lu).
   */
  readonly iniciarPareamento: (opts?: { locale?: string }) => Promise<void>;
  readonly cancelarPareamento: () => void;
  /** Desconecta manualmente (médico clicou no LED verde). Limpa storage. */
  readonly desconectar: () => void;
  /**
   * Reconecta usando lastToken do localStorage (médico clicou no LED vermelho).
   * Verifica via HEAD heartbeat se token ainda existe no backend antes
   * de reabrir SSE. Se token morreu, volta pra idle (médico precisa novo QR).
   */
  readonly reconectar: () => Promise<void>;
}

/* ------------------------------------------------------------------------- */
/* Constantes                                                                  */
/* ------------------------------------------------------------------------- */

/** Endpoint base do worker live (Fase Voice 8.D LIVE).
 *  Mesmo padrão de api/client.ts — Vite injeta VITE_API_BASE em build. */
export const VOICE_API_BASE: string =
  import.meta.env.VITE_API_BASE ?? 'https://drpera-api.luperazolo.workers.dev';

/** URL pública do mic mobile (codificada no QR). */
export const VOICE_MIC_BASE_URL = 'https://ia.drpera.com.br';

export const PAIR_INIT_ENDPOINT = `${VOICE_API_BASE}/api/voice/pair-init`;

/** Constrói URL do stream SSE pro token dado. */
export function buildStreamUrl(token: string): string {
  return `${VOICE_API_BASE}/api/voice/stream/${encodeURIComponent(token)}`;
}

/** Constrói URL do heartbeat HEAD pro token dado. */
export function buildHeartbeatUrl(token: string): string {
  return `${VOICE_API_BASE}/api/voice/token-info/${encodeURIComponent(token)}`;
}

/**
 * Headers de identidade pro voice (trial v2, 02/07/2026).
 *
 * Mesmo padrão de api/chat.ts: logado manda Authorization Bearer (fluxo
 * atual EXATO); anônimo com trial manda X-Anon-Id + X-Trial-Surface: web
 * SEM Authorization. Sem nenhuma identidade → null (caller vira
 * 'login_required', comportamento antigo).
 */
export function buildVoiceIdentityHeaders(
  jwt: string | null,
  trialAnonId: string | null,
): Record<string, string> | null {
  if (jwt !== null) {
    return { Authorization: `Bearer ${jwt}` };
  }
  if (trialAnonId !== null) {
    return { 'X-Anon-Id': trialAnonId, 'X-Trial-Surface': 'web' };
  }
  return null;
}

/* ─── localStorage persistência da sessão voice ───────────────────── */

/** Key no localStorage onde a sessão paireada vive (sobrevive refresh). */
export const VOICE_SESSION_STORAGE_KEY = 'drpera-ia-voice-session-v1';

/** TTL local — 12h match com TTL do backend KV. */
const VOICE_SESSION_TTL_MS = 12 * 3600 * 1000;

export interface PersistedVoiceSession {
  readonly token: string;
  readonly lastEventId: number;
  /** Timestamp em ms do último update — usado pra expirar via TTL local. */
  readonly savedAt: number;
}

/**
 * Lê sessão persistida. Retorna null se ausente, corrompida, ou expirada.
 * (defensivo — localStorage pode falhar em incognito ou storage cheio)
 */
export function loadPersistedSession(): PersistedVoiceSession | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(VOICE_SESSION_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      typeof (parsed as PersistedVoiceSession).token !== 'string' ||
      typeof (parsed as PersistedVoiceSession).lastEventId !== 'number' ||
      typeof (parsed as PersistedVoiceSession).savedAt !== 'number'
    ) {
      return null;
    }
    const session = parsed as PersistedVoiceSession;
    if (Date.now() - session.savedAt > VOICE_SESSION_TTL_MS) {
      // Expirado localmente — limpa pra não acumular lixo.
      try { window.localStorage.removeItem(VOICE_SESSION_STORAGE_KEY); } catch { /* ignore */ }
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

/** Salva sessão. Não-crítico — falhas em localStorage são ignoradas. */
export function persistSession(token: string, lastEventId: number): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const payload: PersistedVoiceSession = {
      token,
      lastEventId,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(VOICE_SESSION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* incognito ou storage cheio — silently skip */
  }
}

/** Apaga sessão persistida (chamado em desconectar manual ou expiração). */
export function clearPersistedSession(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try { window.localStorage.removeItem(VOICE_SESSION_STORAGE_KEY); } catch { /* ignore */ }
}

/* ------------------------------------------------------------------------- */
/* Parser SSE puro (testável)                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Parser puro de chunks SSE. Acumula bytes parciais e retorna eventos
 * completos prontos pra dispatch. Eventos SSE são separados por "\n\n".
 *
 * Formato esperado de cada bloco:
 *   event: <type>
 *   id: <seq>
 *   data: <json>
 *
 * Linhas começando com ":" são comentários (heartbeat keep-alive).
 */
export function parseSSEChunk(buffer: string): {
  readonly events: ReadonlyArray<VoiceSSEEvent>;
  readonly remainder: string;
} {
  const events: VoiceSSEEvent[] = [];
  const blocks: string[] = [];
  let cursor = 0;
  while (cursor < buffer.length) {
    const sepIdx = buffer.indexOf('\n\n', cursor);
    if (sepIdx === -1) break;
    blocks.push(buffer.slice(cursor, sepIdx));
    cursor = sepIdx + 2;
  }
  const remainder = buffer.slice(cursor);

  for (const block of blocks) {
    if (block.trim().length === 0) continue;
    const lines = block.split('\n');
    let type = 'message';
    let id: string | null = null;
    let dataStr = '';
    for (const raw of lines) {
      const line = raw.replace(/\r$/, '');
      if (line.length === 0 || line.startsWith(':')) continue;
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) continue;
      const field = line.slice(0, colonIdx);
      const value = line.slice(colonIdx + 1).replace(/^ /, '');
      if (field === 'event') type = value;
      else if (field === 'id') id = value;
      else if (field === 'data') dataStr += dataStr.length === 0 ? value : `\n${value}`;
    }
    let parsedData: Record<string, unknown> = {};
    if (dataStr.length > 0) {
      try {
        const parsed = JSON.parse(dataStr) as unknown;
        if (parsed !== null && typeof parsed === 'object') {
          parsedData = parsed as Record<string, unknown>;
        }
      } catch {
        // Não-JSON fica em { raw: dataStr }
        parsedData = { raw: dataStr };
      }
    }
    const seq = id !== null ? Number(id) : null;
    events.push({
      type,
      data: parsedData,
      seq: Number.isFinite(seq) ? (seq as number) : null,
    });
  }
  return { events, remainder };
}

/* ------------------------------------------------------------------------- */
/* Mapeador puro: evento SSE → estado pretendido                               */
/* ------------------------------------------------------------------------- */

export interface VoiceEventOutcome {
  readonly nextState: VoiceState | null;       // null = não muda
  readonly error: string | null;
  readonly transcript: { text: string; audioSeconds: number } | null;
  readonly expired: boolean;
}

export function mapVoiceEventToOutcome(ev: VoiceSSEEvent): VoiceEventOutcome {
  switch (ev.type) {
    case 'pairing.connected':
      return { nextState: 'paired', error: null, transcript: null, expired: false };
    case 'recording.started':
      return { nextState: 'recording', error: null, transcript: null, expired: false };
    case 'transcribing':
      return { nextState: 'transcribing', error: null, transcript: null, expired: false };
    case 'transcribed': {
      const text = typeof ev.data.text === 'string' ? ev.data.text : '';
      const audioSeconds =
        typeof ev.data.audioSeconds === 'number'
          ? ev.data.audioSeconds
          : typeof ev.data.audio_seconds === 'number'
          ? ev.data.audio_seconds
          : 0;
      return {
        nextState: 'paired',
        error: null,
        transcript: text.length > 0 ? { text, audioSeconds } : null,
        expired: false,
      };
    }
    case 'error': {
      const msg =
        typeof ev.data.message === 'string'
          ? ev.data.message
          : typeof ev.data.error === 'string'
          ? ev.data.error
          : 'voice_error';
      return { nextState: 'error', error: msg, transcript: null, expired: false };
    }
    case 'expired':
    case 'session.expired':
      return { nextState: 'idle', error: null, transcript: null, expired: true };
    default:
      return { nextState: null, error: null, transcript: null, expired: false };
  }
}

/* ------------------------------------------------------------------------- */
/* Mapeador puro: HTTP status do pair-init → outcome                           */
/* ------------------------------------------------------------------------- */

export interface PairInitResponseBody {
  readonly ok?: boolean;
  readonly token?: string;
  readonly qr_url?: string;
  readonly qrUrl?: string;
  readonly expiresAt?: number;
  readonly error?: string;
  readonly message?: string;
  readonly upgradeUrl?: string;
  readonly upgrade_url?: string;
}

export interface PairInitOutcome {
  readonly state: VoiceState;
  readonly token: string | null;
  readonly qrUrl: string | null;
  readonly expiresAt: number | null;
  readonly error: string | null;
  readonly upgradeUrl: string | null;
}

export function mapPairInitOutcome(
  status: number,
  body: PairInitResponseBody | null,
): PairInitOutcome {
  if (status === 200 && body !== null && typeof body.token === 'string') {
    const qrUrl = body.qr_url ?? body.qrUrl ?? null;
    return {
      state: 'waiting',
      token: body.token,
      qrUrl,
      expiresAt: typeof body.expiresAt === 'number' ? body.expiresAt : null,
      error: null,
      upgradeUrl: null,
    };
  }
  if (status === 403) {
    // Trial v2 (02/07/2026): 'trial_ended' (dia 4+) e 'email_required'
    // (anônimo dia 2-3 / migrado) são roteados pelo App.tsx via `error` —
    // paywall trial e email gate existentes. Estado 'error' é transitório:
    // App fecha o modal de pairing e reseta pra idle assim que detecta.
    if (body?.error === 'trial_ended' || body?.error === 'email_required') {
      return {
        state: 'error',
        token: null,
        qrUrl: null,
        expiresAt: null,
        error: body.error,
        upgradeUrl: null,
      };
    }
    const isTier =
      body?.error === 'tier_upgrade_required' ||
      body?.error === 'tier_blocked' ||
      body?.error === 'feature_not_available';
    if (isTier) {
      return {
        state: 'tier_blocked',
        token: null,
        qrUrl: null,
        expiresAt: null,
        error: body?.message ?? 'tier_upgrade_required',
        upgradeUrl: body?.upgradeUrl ?? body?.upgrade_url ?? null,
      };
    }
    return {
      state: 'error',
      token: null,
      qrUrl: null,
      expiresAt: null,
      error: body?.error ?? 'forbidden',
      upgradeUrl: null,
    };
  }
  if (status === 401) {
    return {
      state: 'error',
      token: null,
      qrUrl: null,
      expiresAt: null,
      error: 'login_required',
      upgradeUrl: null,
    };
  }
  return {
    state: 'error',
    token: null,
    qrUrl: null,
    expiresAt: null,
    error: body?.error ?? 'network_error',
    upgradeUrl: null,
  };
}

/* ------------------------------------------------------------------------- */
/* Conexão SSE manual via fetch + ReadableStream                               */
/* ------------------------------------------------------------------------- */

export interface ConnectSSEOptions {
  readonly url: string;
  /**
   * Headers de identidade — Authorization Bearer (logado) OU
   * X-Anon-Id + X-Trial-Surface (anônimo trial v2).
   */
  readonly authHeaders: Record<string, string>;
  readonly onEvent: (ev: VoiceSSEEvent) => void;
  readonly onError: (err: unknown) => void;
  readonly signal: AbortSignal;
  readonly fetchImpl?: typeof fetch;
  /**
   * Last-Event-ID pra reconnect — backend retoma do `seq > lastEventId`.
   * Default: 0 (stream do início).
   */
  readonly lastEventId?: number;
  /**
   * Callback chamado quando o stream encerra naturalmente (reader.done)
   * sem abort do consumidor. Usado pelo hook pra reconectar transparente
   * após timeout do Worker (~50s). Recebe o último seq visto.
   */
  readonly onStreamEnd?: (lastSeq: number) => void;
}

/**
 * Conecta no SSE via fetch + manual parsing. Retorna promise que resolve
 * quando stream termina (mobile fechou ou abort).
 *
 * EventSource nativo não permite Authorization header customizado em
 * browsers — por isso fazemos manual.
 *
 * BUG FIX 20/05/2026: SSE do Cloudflare Worker tem timeout ~50s. Quando
 * fecha naturalmente (reader.done sem abort), chama `onStreamEnd` com
 * último seq visto pro hook reconectar. Sem isso, a segunda gravação
 * nunca chegava ao PC porque a conexão estava morta.
 */
export async function connectVoiceSSE(opts: ConnectSSEOptions): Promise<void> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const headers: Record<string, string> = {
    ...opts.authHeaders,
    Accept: 'text/event-stream',
  };
  if (typeof opts.lastEventId === 'number' && opts.lastEventId > 0) {
    headers['Last-Event-ID'] = String(opts.lastEventId);
  }
  let lastSeq = opts.lastEventId ?? 0;
  try {
    const res = await fetchImpl(opts.url, {
      method: 'GET',
      headers,
      signal: opts.signal,
    });
    if (!res.ok || res.body === null) {
      opts.onError(new Error(`sse_http_${res.status}`));
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let abortRequested = opts.signal.aborted;
    let streamEnded = false;
    while (!abortRequested) {
      const { value, done } = await reader.read();
      if (done) { streamEnded = true; break; }
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSSEChunk(buffer);
      buffer = parsed.remainder;
      for (const ev of parsed.events) {
        if (ev.seq !== null && ev.seq > lastSeq) lastSeq = ev.seq;
        opts.onEvent(ev);
      }
      abortRequested = opts.signal.aborted;
    }
    // Stream fechou naturalmente (timeout do worker, ~50s). Reconnect.
    if (streamEnded && !opts.signal.aborted) {
      opts.onStreamEnd?.(lastSeq);
    }
  } catch (err) {
    // AbortError esperado quando cancelarPareamento ou unmount
    if (err instanceof Error && err.name === 'AbortError') return;
    opts.onError(err);
  }
}

/* ------------------------------------------------------------------------- */
/* Hook React                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * useVoicePairing — gerencia ciclo de vida do pareamento voice.
 *
 * @param onTranscribed callback opcional disparado em cada 'transcribed'
 * @param fetchImpl injetável pra tests (default fetch global)
 * @param trialAnonId UUID v4 anônimo do trial v2 (02/07/2026). Só é usado
 *        quando NÃO há JWT no localStorage — manda X-Anon-Id +
 *        X-Trial-Surface: web em vez de Authorization (mesmo padrão de
 *        api/chat.ts). null (default) = comportamento atual exato.
 */
export function useVoicePairing(
  onTranscribed?: OnTranscribedCb,
  fetchImpl: typeof fetch = fetch,
  trialAnonId: string | null = null,
): UseVoicePairingResult {
  const [state, setState] = useState<VoiceState>('idle');
  const [token, setToken] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [lastTranscript, setLastTranscript] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgradeUrl, setUpgradeUrl] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const expireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Última seq SSE recebida — usada pra reconectar do ponto certo. */
  const lastEventIdRef = useRef<number>(0);
  /** Token atual — ref pra closures de SSE callback verem valor live. */
  const currentTokenRef = useRef<string | null>(null);
  /** Estado atual — ref pra onError decidir 'disconnected' vs 'error'. */
  const stateRef = useRef<VoiceState>('idle');
  useEffect(() => { stateRef.current = state; }, [state]);

  /** Mantém a referência atualizada do callback do consumidor sem
   *  invalidar identidades de useCallback que dependem dele. */
  const onTranscribedRef = useRef<OnTranscribedCb | undefined>(onTranscribed);
  useEffect(() => {
    onTranscribedRef.current = onTranscribed;
  }, [onTranscribed]);

  /** Limpa qualquer conexão SSE + timer ativos. */
  const teardown = useCallback((): void => {
    if (abortRef.current !== null) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
    if (expireTimerRef.current !== null) {
      clearTimeout(expireTimerRef.current);
      expireTimerRef.current = null;
    }
  }, []);

  const resetSession = useCallback((): void => {
    teardown();
    setToken(null);
    setQrUrl(null);
    setExpiresAt(null);
    setUpgradeUrl(null);
    currentTokenRef.current = null;
    lastEventIdRef.current = 0;
  }, [teardown]);

  const cancelarPareamento = useCallback((): void => {
    resetSession();
    clearPersistedSession();
    setState('idle');
    setError(null);
  }, [resetSession]);

  /**
   * Helper interno — abre SSE com auto-reconnect transparente em
   * timeout do Worker (~50s). Usado por iniciarPareamento (após pair-init
   * sucesso) e por reconectar (após heartbeat HEAD confirmar token vivo).
   *
   * Quando SSE falha por erro de rede DEPOIS de estarmos paired/recording/
   * transcribing → state='disconnected' (recuperável). Quando falha em
   * outros estados (ex: waiting), → state='error' (não recuperável aqui).
   */
  const openSSELoop = useCallback((sessionToken: string, authHeaders: Record<string, string>, initialLastEventId: number): void => {
    const controller = new AbortController();
    abortRef.current = controller;
    currentTokenRef.current = sessionToken;
    lastEventIdRef.current = initialLastEventId;
    const streamUrl = buildStreamUrl(sessionToken);

    const handleEvent = (ev: VoiceSSEEvent): void => {
      // Cancela timer de expiração do QR ao pareamento confirmado.
      if (ev.type === 'pairing.connected' && expireTimerRef.current !== null) {
        clearTimeout(expireTimerRef.current);
        expireTimerRef.current = null;
      }

      // Persiste lastEventId a cada evento — recuperação pós-refresh.
      if (ev.seq !== null && ev.seq > lastEventIdRef.current) {
        lastEventIdRef.current = ev.seq;
        if (currentTokenRef.current !== null) {
          persistSession(currentTokenRef.current, ev.seq);
        }
      }

      const out = mapVoiceEventToOutcome(ev);
      if (out.nextState !== null) setState(out.nextState);
      if (out.error !== null) setError(out.error);
      if (out.transcript !== null) {
        setLastTranscript(out.transcript.text);
        try {
          onTranscribedRef.current?.(out.transcript.text, out.transcript.audioSeconds);
        } catch {
          /* erro do consumidor não derruba SSE */
        }
      }
      if (out.expired) {
        resetSession();
        clearPersistedSession();
      }
    };

    const loop = (lastEventId: number): void => {
      void connectVoiceSSE({
        url: streamUrl,
        authHeaders,
        signal: controller.signal,
        fetchImpl,
        lastEventId,
        onEvent: handleEvent,
        onStreamEnd: (lastSeq) => {
          // Worker timeout (~50s) — reconect transparente.
          if (!controller.signal.aborted) {
            loop(lastSeq);
          }
        },
        onError: () => {
          // Se já estávamos paired, marca disconnected (recuperável).
          // Se em outro estado (waiting), mantém error.
          const cur = stateRef.current;
          if (cur === 'paired' || cur === 'recording' || cur === 'transcribing') {
            setState('disconnected');
            setError('connection_lost');
          } else {
            setState('error');
            setError('stream_error');
          }
        },
      });
    };
    loop(initialLastEventId);
  }, [fetchImpl, resetSession]);

  const iniciarPareamento = useCallback(async (opts?: { locale?: string }): Promise<void> => {
    teardown();
    setError(null);
    setUpgradeUrl(null);
    setLastTranscript(null);
    setState('requesting');

    // Web: JWT lido sincronamente do localStorage (diferente da extensão,
    // que usa chrome.storage.local async via useAuth.getToken).
    // Trial v2: sem JWT mas com trialAnonId → headers anônimos (server decide).
    const authHeaders = buildVoiceIdentityHeaders(getStoredJwt(), trialAnonId);
    if (authHeaders === null) {
      setState('error');
      setError('login_required');
      return;
    }

    let res: Response;
    try {
      res = await fetchImpl(PAIR_INIT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({ baseUrl: VOICE_MIC_BASE_URL }),
      });
    } catch {
      setState('error');
      setError('network_error');
      return;
    }

    // Parse opcional do body — se falhar (resposta vazia/HTML), segue com null.
    // O mapPairInitOutcome aceita null e devolve fallback de erro genérico.
    const body: PairInitResponseBody | null = await res
      .json()
      .then((v: unknown) => v as PairInitResponseBody)
      .catch(() => null);

    const outcome = mapPairInitOutcome(res.status, body);
    setState(outcome.state);
    setToken(outcome.token);
    // FIX 23/05 noite (Dr. Lu): backend retorna qrUrl como
    // https://ia.drpera.com.br/mic/<token>. Anexa ?lang=<locale> AQUI
    // (não no baseUrl — quebrava URL pois `?` vinha antes de /mic/).
    const qrUrlFinal = opts?.locale && outcome.qrUrl !== null
      ? `${outcome.qrUrl}?lang=${encodeURIComponent(opts.locale)}`
      : outcome.qrUrl;
    setQrUrl(qrUrlFinal);
    setExpiresAt(outcome.expiresAt);
    setError(outcome.error);
    setUpgradeUrl(outcome.upgradeUrl);

    if (outcome.state !== 'waiting' || outcome.token === null) {
      return;
    }

    // Agenda timer de expiração — quando passar, volta pra idle
    if (outcome.expiresAt !== null) {
      const delay = Math.max(0, outcome.expiresAt - Date.now());
      expireTimerRef.current = setTimeout(() => {
        resetSession();
        setState('idle');
      }, delay);
    }

    // Abre SSE com reconnect automático no timeout do worker (~50s).
    // Cada conexão SSE individual dura ~50s; openSSELoop reconecta com
    // Last-Event-ID retomando da mesma seq.
    openSSELoop(outcome.token, authHeaders, 0);
  }, [fetchImpl, openSSELoop, resetSession, teardown, trialAnonId]);

  /**
   * Desconecta manualmente (médico clicou no LED verde).
   * Fecha SSE, limpa storage, volta pra idle.
   *
   * NOTA: NÃO chama backend pra invalidar token — TTL KV cuida.
   * Próximo `iniciarPareamento` cria token novo do zero.
   */
  const desconectar = useCallback((): void => {
    resetSession();
    clearPersistedSession();
    setState('idle');
    setError(null);
    setLastTranscript(null);
  }, [resetSession]);

  /**
   * Reconecta usando lastToken do localStorage (médico clicou no LED vermelho).
   *
   * Fluxo:
   *   1. Lê sessão persistida. Se não houver → volta idle.
   *   2. HEAD /api/voice/token-info/:token com JWT.
   *      - 200 → reabre SSE com lastEventId persistido (continua de onde
   *        parou — nenhum evento perdido).
   *      - 401 → JWT inválido (logout?) → erro 'login_required'.
   *      - 403 → token de outro user (storage corrompido) → limpa + idle.
   *      - 404 → token morreu no backend → limpa + idle (médico precisa
   *        novo QR).
   */
  const reconectar = useCallback(async (): Promise<void> => {
    setError(null);
    const persisted = loadPersistedSession();
    if (persisted === null) {
      desconectar();
      return;
    }

    // Trial v2: mesma resolução de identidade do iniciarPareamento.
    const authHeaders = buildVoiceIdentityHeaders(getStoredJwt(), trialAnonId);
    if (authHeaders === null) {
      setState('error');
      setError('login_required');
      return;
    }

    setState('requesting');
    let res: Response;
    try {
      res = await fetchImpl(buildHeartbeatUrl(persisted.token), {
        method: 'HEAD',
        headers: authHeaders,
      });
    } catch {
      // Heartbeat falhou por rede — mantém disconnected, médico tenta de novo.
      setState('disconnected');
      setError('network_error');
      return;
    }

    if (res.status === 200) {
      // Token vivo no backend. Reabre SSE com lastEventId persistido.
      setToken(persisted.token);
      setState('paired');
      openSSELoop(persisted.token, authHeaders, persisted.lastEventId);
      return;
    }

    if (res.status === 401) {
      setState('error');
      setError('login_required');
      return;
    }

    // 403 (token alheio — improvável) ou 404 (expirou) — precisa novo QR.
    clearPersistedSession();
    resetSession();
    setState('idle');
    setError(res.status === 404 ? 'session_expired' : 'session_invalid');
  }, [desconectar, fetchImpl, openSSELoop, resetSession, trialAnonId]);

  // Cleanup no unmount — fecha qualquer conexão ativa
  useEffect(() => {
    return () => {
      teardown();
    };
  }, [teardown]);

  // Mount — detecta sessão persistida (post-refresh) e sinaliza disconnected
  // pro médico saber que dá pra reconectar via click sem refazer QR.
  // Roda só uma vez via ref guard (evita loop em React StrictMode).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    const persisted = loadPersistedSession();
    if (persisted !== null) {
      setToken(persisted.token);
      setState('disconnected');
      // Não chama reconectar() automaticamente — médico decide via click no LED.
      // (auto-reconnect on mount poderia consumir bandwidth desnecessária se
      // o médico só abriu /ia rapidamente sem intenção de usar voice).
    }
  }, []);

  return {
    state,
    token,
    qrUrl,
    expiresAt,
    lastTranscript,
    error,
    upgradeUrl,
    iniciarPareamento,
    cancelarPareamento,
    desconectar,
    reconectar,
  };
}
